/* eslint-disable max-lines */
import { useContext, useEffect } from 'react';
import useProduct from './useProduct';
import useBookingPrices from './use-booking-prices';
import useLocale from './use-locale';
import {
  formatPricePixel,
  formatPriceTagManager,
  getCategory,
  getProduct,
  setPixel,
  setTagManager,
  trackPixelEvent,
  trackTagManagerEvent,
  trackTikTokPixelEvent,
} from '../Utils/tracking';
import { bookingState } from '../Pages/Booking/client-state/booking-state-context';
import useBookingShop from '../Pages/Booking/server-data/use-booking-shop';
import useBookingMinimalShop from '../Pages/Booking/server-data/use-booking-minimal-shop';
import { STAPE_GTM_ID } from '../config';

interface bookingTrackAction {
  type: string;
  product?: any;
  step?: any;
  paymentType?: any;
  subscribed?: any;
  duration?: any;
  boatCount?: any;
  newStep?: number;
  item?: {
    id: string;
    quantity?: number;
  };
  location?: string;
  date?: Date;
  time?: string;
  itemCategory?: string;
  voucher?: string;
}

export type bookingTrackFunction = (action: bookingTrackAction) => void;

const useBookingTracking = () => {
  const { data: minimalShop } = useBookingMinimalShop();
  const { data: shop } = useBookingShop();
  const { state } = useContext(bookingState);
  const { language } = useLocale();
  const product = useProduct();
  const { bookingPrices } = useBookingPrices();

  const items = shop?.items ?? [];

  // When shop changes, FB Pixel
  useEffect(() => {
    if (shop) {
      if (process.env.REACT_APP_ANALYTICS_ENABLED === 'true') {
        if (shop?.configuration?.gtmId && shop.configuration.gtmId !== STAPE_GTM_ID) {
          setTagManager(shop.configuration.gtmId, language);
        }
        if (shop?.configuration?.pixelId) {
          setPixel(shop?.configuration?.pixelId);
        }
      } else {
        console.log(
          `GTM ID: ${shop?.configuration?.gtmId}, Pixel ID: ${shop?.configuration?.pixelId}`
        );
      }
    }
  }, [shop, language]);

  const shopId = shop?.id;
  const shopName = shop?.name;

  // Listen for events to be tracked
  const track: bookingTrackFunction = (action) => {
    try {
      switch (action.type) {
        case 'PRODUCT_VIEW': {
          const { name, id } = getProduct({
            type: product,
            duration: action.product.duration,
          });

          const event = {
            event: 'productView',
            ecommerce: {
              detail: {
                actionField: { step: '1' },
                products: [
                  {
                    name,
                    id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(action.product.price),
                    category: getCategory(product),
                    quantity: action.product.boatCount,
                  },
                ],
              },
            },
          };

          trackTikTokPixelEvent('ViewContent');
          trackPixelEvent('ViewContent', undefined);
          return trackTagManagerEvent(event);
        }

        case 'ADD_TO_CART': {
          const { name, id } = getProduct({
            type: product,
            duration: action.product.duration,
          });

          const event = {
            event: 'addToCart',
            ecommerce: {
              currencyCode: minimalShop?.currency,
              add: {
                products: [
                  {
                    name,
                    id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(action.product.price),
                    category: getCategory(product),
                    quantity: action.product.boatCount,
                  },
                ],
              },
            },
          };

          const pixelObject = {
            value: formatPricePixel(action.product.price),
            currency: minimalShop?.currency,
            content_name: name,
            content_type: 'product',
            contents: [
              {
                id,
                quantity: action.product.boatCount,
                item_price: formatPricePixel(action.product.price),
              },
            ],
          };

          const tikTokPixelData = {
            content_type: 'product',
            quantity: action.product.boatCount,
            description: `${action.product.duration}h booking`,
            content_id: id,
            currency: minimalShop?.currency,
            value: formatPricePixel(action.product.price),
          };

          trackTikTokPixelEvent('AddToCart', tikTokPixelData);
          trackPixelEvent('AddToCart', pixelObject);
          return trackTagManagerEvent(event);
        }

        case 'CHECKOUT': {
          const { name, id } = getProduct({
            type: product,
            duration: action.product.duration,
          });

          const event: any = {
            event: 'checkout',
            ecommerce: {
              checkout: {
                actionField: action.paymentType
                  ? { step: action.step, option: action.paymentType }
                  : { step: action.step },
                products: [
                  {
                    name,
                    id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(
                      (bookingPrices.sailingPrice || 0) / action.product.boatCount
                    ),
                    category: getCategory(product),
                    quantity: action.product.boatCount,
                  },
                ],
              },
            },
            user_data: {},
          };

          // add user data to GTM event
          if (
            state.customer.customer.firstname &&
            state.customer.customer.lastname &&
            state.customer.customer.email &&
            state.customer.customer.phone
          ) {
            event.user_data = {
              email: state.customer.customer.email.toLowerCase(),
              phone_number: state.customer.customer.phone,
              address: [
                {
                  first_name: state.customer.customer.firstname.toLowerCase(),
                  last_name: state.customer.customer.lastname.toLowerCase(),
                },
              ],
            };

            if (state.customer.customer.address?.country) {
              event.user_data.address[0].country =
                state.customer.customer.address?.country.toLowerCase();
            }
          }

          if (state.isWarrantyApplied) {
            const warranty = getProduct({ type: 'warranty' });

            const item = {
              name: warranty.name,
              id: warranty.id,
              shopId,
              shopName,
              price: formatPriceTagManager(
                (bookingPrices.warrantyPrice || 0) / action.product.boatCount
              ),
              category: warranty.name,
              quantity: action.product.boatCount,
            };

            event.ecommerce.checkout.products.push(item);
          }

          if (action.step === 1) {
            trackPixelEvent('InitiateCheckout', undefined);
            trackTikTokPixelEvent('InitiateCheckout');
          }
          if (action.step === 5) {
            trackPixelEvent('AddPaymentInfo', undefined);
            trackTikTokPixelEvent('AddPaymentInfo');
          }

          return trackTagManagerEvent(event);
        }

        case 'ADD_SHIPPING_INFO': {
          const { name, id } = getProduct({
            type: product,
            duration: action.product.duration,
          });

          const event = {
            event: 'add_shipping_info',
            ecommerce: {
              checkout: {
                products: [
                  {
                    name,
                    id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(
                      (bookingPrices.sailingPrice || 0) / action.product.boatCount
                    ),
                    category: getCategory(product),
                    quantity: action.product.boatCount,
                  },
                ],
              },
            },
          };

          if (state.isWarrantyApplied) {
            const warranty = getProduct({ type: 'warranty' });

            const item = {
              name: warranty.name,
              id: warranty.id,
              shopId,
              shopName,
              price: formatPriceTagManager(
                (bookingPrices.warrantyPrice || 0) / action.product.boatCount
              ),
              category: warranty.name,
              quantity: action.product.boatCount,
            };

            event.ecommerce.checkout.products.push(item);
          }

          return trackTagManagerEvent(event);
        }

        case 'TRANSACTION_COMPLETE': {
          if (shop?.configuration?.enableServerSideTracking) {
            // If server-side tracking is enabled, we don't track the event here
            return null;
          }
          const { name, id } = getProduct({
            type: product,
            duration: action.product.duration,
          });

          // create GTM event
          const event: any = {
            event: 'transactionComplete',
            ecommerce: {
              currencyCode: minimalShop?.currency,
              purchase: {
                actionField:
                  state.vouchers.length > 0
                    ? {
                        id: action.product.id,
                        revenue: formatPriceTagManager(bookingPrices.fullPrice || 0),
                        coupon: state.vouchers[0].code,
                      }
                    : {
                        id: action.product.id,
                        revenue: formatPriceTagManager(bookingPrices.fullPrice || 0),
                      },
                products: [
                  {
                    name,
                    id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(
                      (bookingPrices.sailingPrice || 0) / action.product.boatCount
                    ),
                    category: getCategory(product),
                    quantity: action.product.boatCount,
                    dimension1: 'Online',
                  },
                ],
              },
            },
          };

          // add user data to GTM event
          if (
            state.customer.customer.firstname &&
            state.customer.customer.lastname &&
            state.customer.customer.email &&
            state.customer.customer.phone
          ) {
            event.user_data = {
              email: state.customer.customer.email.toLowerCase(),
              phone_number: state.customer.customer.phone,
              address: [
                {
                  first_name: state.customer.customer.firstname.toLowerCase(),
                  last_name: state.customer.customer.lastname.toLowerCase(),
                },
              ],
            };

            if (state.customer.customer.address?.country) {
              event.user_data.address[0].country =
                state.customer.customer.address?.country.toLowerCase();
            }
          }

          // add warranty to GTM event
          if (state.isWarrantyApplied) {
            const warranty = getProduct({ type: 'warranty' });

            const item = {
              name: warranty.name,
              id: warranty.id,
              shopId,
              shopName,
              price: formatPriceTagManager(
                (bookingPrices.warrantyPrice || 0) / action.product.boatCount
              ),
              category: warranty.name,
              quantity: action.product.boatCount,
              dimension1: 'Online',
            };

            event.ecommerce.purchase.products.push(item);
          }

          // add extras to GTM event
          if (state.order.length > 0 && items && items.length > 0) {
            for (const addon of state.order) {
              const item = {
                name: items.find((item) => item.id === addon.itemId)?.trackingName ?? '',
                id: addon.itemId,
                shopId,
                shopName,
                price: formatPriceTagManager(
                  items.find((item) => item.id === addon.itemId)?.price ?? 0
                ),
                category: 'Extras',
                quantity: addon.quantity,
                dimension1: 'Online',
              };

              event.ecommerce.purchase.products.push(item);
            }
          }

          // create FB pixel object
          const pixelObject = {
            value: formatPricePixel(bookingPrices.fullPrice || 0),
            currency: minimalShop?.currency,
            content_name: name,
            content_type: 'product',
            contents: [
              {
                id,
                quantity: action.product.boatCount,
                item_price: formatPricePixel(
                  (bookingPrices.sailingPrice || 0) / action.product.boatCount
                ),
              },
            ],
          };

          // add warranty to FB pixel object
          if (state.isWarrantyApplied) {
            const warranty = getProduct({ type: 'warranty' });

            const item = {
              id: warranty.id,
              quantity: action.product.boatCount,
              item_price: formatPricePixel(
                (bookingPrices.warrantyPrice || 0) / action.product.boatCount
              ),
            };

            pixelObject.contents.push(item);
          }

          // add extras to FB pixel object
          if (state.order.length > 0 && items && items.length > 0) {
            for (const addon of state.order) {
              const item = {
                id: addon.itemId,
                quantity: addon.quantity,
                item_price: formatPricePixel(
                  items.find((item) => item.id === addon.itemId)?.price ?? 0
                ),
              };

              pixelObject.contents.push(item);
            }
          }

          // prepare tikTokPixel data
          const tikTokPixelData = [
            {
              content_type: 'product',
              price: formatPricePixel(
                (bookingPrices.sailingPrice || 0) / action.product.boatCount
              ),
              currency: minimalShop?.currency,
              quantity: action.product.boatCount,
              description: `${action.product.duration}h booking`,
              content_id: id,
            },
          ];

          // add warranty to tik tok pixel data
          if (state.isWarrantyApplied) {
            const warranty = getProduct({ type: 'warranty' });

            const item = {
              content_type: 'product',
              price: formatPricePixel(
                (bookingPrices.warrantyPrice || 0) / action.product.boatCount
              ),
              currency: minimalShop?.currency,
              quantity: action.product.boatCount,
              description: 'Warranty',
              content_id: warranty.id,
            };

            tikTokPixelData.push(item);
          }

          // add extras to tik tok pixel data
          if (state.order.length > 0 && items && items.length > 0) {
            for (const addon of state.order) {
              const productItem = items.find((item) => item.id === addon.itemId);
              const item = {
                content_type: 'product',
                price: formatPricePixel(productItem?.price || 0),
                currency: minimalShop?.currency,
                quantity: addon.quantity,
                description: productItem?.name ?? '',
                content_id: addon.itemId,
              };

              tikTokPixelData.push(item);
            }
          }

          if (tikTokPixelData.length > 1) {
            trackTikTokPixelEvent('CompletePayment', {
              contents: tikTokPixelData,
              value: formatPricePixel(bookingPrices.fullPrice || 0),
              currency: minimalShop?.currency,
            });
          } else {
            trackTikTokPixelEvent('CompletePayment', {
              ...tikTokPixelData[0],
              value: formatPricePixel(bookingPrices.fullPrice || 0),
            });
          }

          trackPixelEvent('Purchase', pixelObject);
          return trackTagManagerEvent(event);
        }

        case 'SUBSCRIBE_TO_NEWSLETTER': {
          const event = {
            event: 'subscribedToNewsletter',
            subscribed: action.subscribed,
          };

          trackTikTokPixelEvent('Subscribe');
          return trackTagManagerEvent(event);
        }

        case 'CHANGE_DURATION': {
          const event = {
            event: 'changeDuration',
            newDuration: action.duration,
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_BOAT_COUNT': {
          const event = {
            event: 'changeBoatCount',
            newBoatCount: action.boatCount,
          };

          return trackTagManagerEvent(event);
        }

        case 'NAVIGATE_BACK': {
          const event = {
            event: 'navigate_back',
            new_step: action.newStep,
          };

          return trackTagManagerEvent(event);
        }

        case 'OPEN_CART_SHEET': {
          const event = {
            event: 'open_cart_sheet',
          };

          return trackTagManagerEvent(event);
        }

        case 'ADD_ITEM_TO_CART': {
          const item = items.find((item) => item.id === action.item?.id);

          const event = {
            event: 'addToCart',
            ecommerce: {
              currencyCode: minimalShop?.currency,
              add: {
                products: [
                  {
                    name: item?.name,
                    id: item?.id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(item?.price ?? 0),
                    category: item?.categoryNames?.[0] ?? '',
                    quantity: action.item?.quantity,
                  },
                ],
              },
            },
          };

          return trackTagManagerEvent(event);
        }

        case 'REMOVE_ITEM_FROM_CART': {
          const item = items.find((item) => item.id === action.item?.id);

          const event = {
            event: 'remove_from_cart',
            ecommerce: {
              currencyCode: minimalShop?.currency,
              remove: {
                products: [
                  {
                    name: item?.name,
                    id: item?.id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(item?.price ?? 0),
                    category: item?.categoryNames?.[0] ?? '',
                    quantity: action.item?.quantity,
                  },
                ],
              },
            },
          };

          return trackTagManagerEvent(event);
        }

        case 'ADD_WARRANTY_TO_CART': {
          const warranty = getProduct({ type: 'warranty' });

          const event = {
            event: 'addToCart',
            ecommerce: {
              currencyCode: minimalShop?.currency,
              add: {
                products: [
                  {
                    name: warranty.name,
                    id: warranty.id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(
                      (bookingPrices.warrantyPrice || 0) / action.boatCount
                    ),
                    category: warranty.name,
                    quantity: action.boatCount,
                  },
                ],
              },
            },
          };

          return trackTagManagerEvent(event);
        }

        case 'REMOVE_WARRANTY_FROM_CART': {
          const warranty = getProduct({ type: 'warranty' });

          const event = {
            event: 'remove_from_cart',
            ecommerce: {
              currencyCode: minimalShop?.currency,
              remove: {
                products: [
                  {
                    name: warranty.name,
                    id: warranty.id,
                    shopId,
                    shopName,
                    price: formatPriceTagManager(
                      (bookingPrices.warrantyPrice || 0) / action.boatCount
                    ),
                    category: warranty.name,
                    quantity: action.boatCount,
                  },
                ],
              },
            },
          };

          return trackTagManagerEvent(event);
        }

        case 'GO_TO_LOCATION_PAGE': {
          const event = {
            event: 'go_to_location_page',
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_LOCATION': {
          const event = {
            event: 'change_location',
            location: action.location,
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_PICKUP_LOCATION': {
          const event = {
            event: 'change_pickup_location',
            location: action.location,
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_DROPOFF_LOCATION': {
          const event = {
            event: 'change_dropoff_location',
            location: action.location,
          };

          return trackTagManagerEvent(event);
        }

        case 'GO_TO_DATE_TIME_PAGE': {
          const event = {
            event: 'go_to_date_time_page',
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_DATE': {
          const event = {
            event: 'change_date',
            location: action.date,
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_DEPARTURE_TIME': {
          const event = {
            event: 'change_departure_time',
            location: action.time,
          };

          return trackTagManagerEvent(event);
        }

        case 'GO_TO_EXTRAS_PAGE': {
          const event = {
            event: 'go_to_extras_page',
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_ITEM_CATEGORY': {
          const event = {
            event: 'change_item_category',
            item_category: action.itemCategory,
          };

          return trackTagManagerEvent(event);
        }

        case 'GO_TO_CUSTOMER_FORM_PAGE': {
          const event = {
            event: 'go_to_customer_form_page',
          };

          return trackTagManagerEvent(event);
        }

        case 'CUSTOMER_FORM_FILLED': {
          const event = {
            event: 'customer_form_filled',
          };

          return trackTagManagerEvent(event);
        }

        case 'GO_TO_CHECKOUT_PAGE': {
          const event = {
            event: 'go_to_checkout_page',
          };

          return trackTagManagerEvent(event);
        }

        case 'OPEN_VOUCHER_FORM': {
          const event = {
            event: 'open_voucher_form',
          };

          return trackTagManagerEvent(event);
        }

        case 'CANCEL_APPLY_VOUCHER': {
          const event = {
            event: 'cancel_apply_voucher',
          };

          return trackTagManagerEvent(event);
        }

        case 'VOUCHER_FILLED': {
          const event = {
            event: 'voucher_filled',
          };

          return trackTagManagerEvent(event);
        }

        case 'APPLY_VOUCHER': {
          const event = {
            event: 'apply_voucher',
            voucher: action.voucher,
          };

          return trackTagManagerEvent(event);
        }

        case 'ACCEPT_TERMS': {
          const event = {
            event: 'accept_terms',
          };

          return trackTagManagerEvent(event);
        }

        case 'NEWSLETTER_PRE_SELECTION': {
          const event = {
            event: 'newsletter_pre_selection',
            subscribed: action.subscribed,
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_NEWSLETTER': {
          const event = {
            event: 'change_newsletter',
            subscribed: action.subscribed,
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_PAYMENT_METHOD': {
          const event = {
            event: 'change_payment_method',
            payment_method: action.paymentType,
          };

          return trackTagManagerEvent(event);
        }

        case 'VIEW_ITEM_DETAIL': {
          const item = items.find((item) => item.id === action.item?.id);

          const event = {
            event: 'view_item_detail',
            item: {
              name: item?.name,
              id: item?.id,
              shopId,
              shopName,
              price: formatPriceTagManager(item?.price ?? 0),
              category: item?.categoryNames?.[0] ?? '',
              quantity: 1,
            },
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_ITEM_QUANTITY': {
          const item = items.find((item) => item.id === action.item?.id);

          const event = {
            event: 'change_item_quantity',
            item: {
              name: item?.name,
              id: item?.id,
              shopId,
              shopName,
              price: formatPriceTagManager(item?.price ?? 0),
              category: item?.categoryNames?.[0] ?? '',
              quantity: action.item?.quantity,
            },
          };

          return trackTagManagerEvent(event);
        }

        case 'CHANGE_ITEMS_PAGE': {
          const event = {
            event: 'change_items_page',
            page: action.newStep,
          };

          return trackTagManagerEvent(event);
        }

        default: {
          // return error, unvalid action.type
          return null;
        }
      }
    } catch (error) {
      console.error('tracking error', error);
    }
  };

  return track;
};

export default useBookingTracking;
